import * as React from "react";
import { cn } from "@shared/utils";
import { createComponent } from "@shared/utils/src/createComponent";

/**
 * `Table` wraps a native `<table>` in a scrollable container with consistent styles.
 *
 * @example
 * ```tsx
 * <Table>
 *   <TableHeader>...</TableHeader>
 *   <TableBody>...</TableBody>
 * </Table>
 * ```
 */
const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

/**
 * `TableHeader` represents the `<thead>` element.
 * Adds styling for row borders in the header section.
 */
const TableHeader = createComponent(
  "thead",
  "[&_tr]:border-b",
  "TableHeader"
);

/**
 * `TableBody` represents the `<tbody>` section of the table.
 * Ensures the last row has no bottom border.
 */
const TableBody = createComponent(
  "tbody",
  "[&_tr:last-child]:border-0",
  "TableBody"
);

/**
 * `TableFooter` wraps the `<tfoot>` element.
 * Applies muted background and spacing.
 */
const TableFooter = createComponent(
  "tfoot",
  "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
  "TableFooter"
);

/**
 * `TableRow` wraps a `<tr>` element with hover styles and row state support.
 */
const TableRow = createComponent(
  "tr",
  "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
  "TableRow"
);

/**
 * `TableHead` renders a `<th>` element with proper alignment and focus styles.
 */
const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-10 px-2 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

/**
 * `TableCell` wraps a `<td>` with styling for alignment and checkbox content.
 */
const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

/**
 * `TableCaption` is a `<caption>` element for providing table descriptions.
 */
const TableCaption = createComponent(
  "caption",
  "mt-4 text-sm text-muted-foreground",
  "TableCaption"
);

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
};
